/**
 * Affiliate API สำหรับ user-modern — endpoints เดียวกับ user-classic
 * /api/v1/user-auth/affiliate/me, members, commission-history
 */
import { handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

function getAccessToken(): string | null {
  return localStorage.getItem('accessToken')
}

function getCurrentUser(): { id?: string; _id?: string; tenantId?: string } | null {
  try {
    const s = localStorage.getItem('user')
    return s ? JSON.parse(s) : null
  } catch {
    return null
  }
}

function getTenantId(): string {
  const user = getCurrentUser()
  const fromUser = user?.tenantId
  if (fromUser) return fromUser
  const resolved = getRuntimeTenantIdSync()
  if (resolved) return resolved
  throw new Error('Tenant ID is required but not found.')
}

async function affiliateFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken()
  const tenantId = getTenantId()
  const url = `${getBaseURL()}${endpoint}`
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Tenant-ID': tenantId,
      ...(options?.headers as Record<string, string>),
    },
    credentials: 'include',
  })
  const data = (await res.json()) as T & { code?: string; message?: string }
  if (res.status === 401 && (data as any)?.code === 'SESSION_REPLACED') {
    await handleSessionReplaced()
    throw new Error('SESSION_REPLACED')
  }
  if (!res.ok) {
    const err = new Error((data as any)?.message || `HTTP ${res.status}`) as Error & { status?: number }
    ;(err as any).status = res.status
    throw err
  }
  return data
}

const AFFILIATE_ME_CACHE_MS = 60 * 1000
let affiliateMeCache: { data: AffiliateMeData; at: number } | null = null
/** ลด 429: รวม request ที่กำลังโหลดอยู่ (รวมถึงตอน React Strict Mode ยิง effect สองครั้ง) */
let affiliateMeInFlight: Promise<AffiliateMeData> | null = null
let commissionHistoryInFlight: Promise<AffiliateCommissionHistoryItem[]> | null = null
let membersInFlight: Promise<AffiliateMemberItem[]> | null = null

export interface AffiliateMeData {
  affiliateCode: string
  affiliateLink: string
  commissionRate: string
  referredMembers: number
  directReferredMembers?: number
  totalEarnings: number
  remainingEarnings: number
  calculationBasis?: 'loss' | 'mixed'
  totalsBySource?: {
    lottery?: number
    amb?: number
  }
}

export interface AffiliateMemberItem {
  username: string
  displayName: string
  joinedAt: string
  status: string
}

export interface AffiliateCommissionHistoryItem {
  _id?: string
  date?: string
  betAmount?: number
  commission?: number
  createdAt?: string
  sourceType?: 'lottery' | 'amb'
  referredUsername?: string
  referredDisplayName?: string
  level?: number
  category?: string
  providerId?: string
  providerName?: string
}

export interface AffiliateMeResponse {
  success: boolean
  data: AffiliateMeData
  message?: string
}

export interface AffiliateMembersResponse {
  success: boolean
  data: AffiliateMemberItem[]
  message?: string
}

export interface AffiliateCommissionHistoryResponse {
  success: boolean
  data: AffiliateCommissionHistoryItem[]
  message?: string
}

/**
 * แปลง ref token จากลิงก์ ?ref=... เป็น referralCode สำหรับ flow สมัครสมาชิก
 * ใช้กับผู้ใช้ที่ยังไม่ login จึงเรียกแบบ public โดยไม่ต้องมี access token
 */
export async function resolveReferralToken(refToken: string): Promise<{ referralCode: string; tenantId?: string }> {
  const tenantId = getRuntimeTenantIdSync()
  const res = await fetch(`${getBaseURL()}/api/v1/user-auth/affiliate/ref/resolve?ref=${encodeURIComponent(refToken)}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      'X-User-Role': 'anonymous',
    },
    credentials: 'include',
  })
  const data = await res.json().catch(() => ({})) as {
    success?: boolean
    data?: { referralCode?: string; tenantId?: string }
    message?: string
  }
  if (!res.ok || !data.success || !data.data?.referralCode) {
    throw new Error(data.message || 'Invalid or expired referral link')
  }
  return { referralCode: data.data.referralCode, tenantId: data.data.tenantId }
}

export async function getAffiliateMe(forceRefresh = false): Promise<AffiliateMeData> {
  if (!forceRefresh && affiliateMeCache && Date.now() - affiliateMeCache.at < AFFILIATE_ME_CACHE_MS) {
    return affiliateMeCache.data
  }
  if (!forceRefresh && affiliateMeInFlight) return affiliateMeInFlight
  const promise = (async () => {
    try {
      const res = await affiliateFetch<AffiliateMeResponse>('/api/v1/user-auth/affiliate/me')
      if (!res.success || !res.data) {
        throw new Error((res as any).message || 'Failed to load affiliate data')
      }
      affiliateMeCache = { data: res.data, at: Date.now() }
      return res.data
    } finally {
      affiliateMeInFlight = null
    }
  })()
  affiliateMeInFlight = promise
  return promise
}

export function clearAffiliateMeCache(): void {
  affiliateMeCache = null
}

export async function getAffiliateMembers(): Promise<AffiliateMemberItem[]> {
  if (membersInFlight) return membersInFlight
  const promise = (async () => {
    try {
      const res = await affiliateFetch<AffiliateMembersResponse>('/api/v1/user-auth/affiliate/members')
      if (!res.success) {
        throw new Error((res as any).message || 'Failed to load referred members')
      }
      return Array.isArray(res.data) ? res.data : []
    } finally {
      membersInFlight = null
    }
  })()
  membersInFlight = promise
  return promise
}

export async function getAffiliateCommissionHistory(limit?: number): Promise<AffiliateCommissionHistoryItem[]> {
  if (commissionHistoryInFlight) return commissionHistoryInFlight
  const qs = limit != null ? `?limit=${Math.min(limit, 100)}` : ''
  const promise = (async () => {
    try {
      const res = await affiliateFetch<AffiliateCommissionHistoryResponse>(
        `/api/v1/user-auth/affiliate/commission-history${qs}`
      )
      if (!res.success) {
        throw new Error((res as any).message || 'Failed to load commission history')
      }
      return Array.isArray(res.data) ? res.data : []
    } finally {
      commissionHistoryInFlight = null
    }
  })()
  commissionHistoryInFlight = promise
  return promise
}

export interface AffiliateWithdrawResponse {
  success: boolean
  pendingApproval?: boolean
  message?: string
  data?: {
    mode?: 'manual' | 'auto'
    paidAmount?: number
    lockedAmount?: number
    commissionCount?: number
    requestId?: string
    failedCount?: number
  }
}

export async function affiliateWithdrawRequest(amount: number): Promise<AffiliateWithdrawResponse> {
  const res = await affiliateFetch<AffiliateWithdrawResponse>('/api/v1/user-auth/affiliate/withdraw-request', {
    method: 'POST',
    body: JSON.stringify({ amount }),
  })
  return res
}
